import { Box, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import InfoTooltip from "../../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import ZonedSalaryValue from "../../../../../../common/components/functional/ZonedSalaryValue";
import FormGridRow from "../../../../../../common/components/functional/formGridRow/FormGridRow";
import FormSection from "../../../../../../common/components/functional/formSection/FormSection";
import FormTable, {
  DataItem,
} from "../../../../../../common/components/functional/formTable/FormTable";
import { leadingZerosDateFormat } from "../../../../../../common/helpers/formatDateTime";
import {
  calculateTotalSalary,
  calculateZonedTotalSalary,
} from "../../../../../../common/utils/salaryUtils";
import { useConfirmationDialog } from "../../../../../../contexts/ConfirmationDialogContext";
import { fetchEmploymentAttributesForEditing } from "../../../../../employment/employmentSlice";
import TargetedShiftSalaryDialog from "../../../../../employment/targetedShiftSalaries/TargetedShiftSalaryDialog";
import {
  buildSalaryKey,
  getActiveSalaryRange,
  hasMultipleDateRanges,
} from "../../../../../employment/targetedShiftSalaries/helpers";
import {
  TargetedShiftSalaryRow,
  TargetedShiftSpec,
  TargetingKeys,
} from "../../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import { TargetedShiftSalaryDialogState } from "../../../../../employment/targetedShiftSalaries/types";
import { fetchEezyCompanies } from "../../../../../invoices/invoiceSlice";
import { RuleFormValues } from "../../../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/RuleEditDialog";
import { RulesTable } from "../../../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/RulesTable";
import { ContentKey } from "../../../../../talentProfile/TalentDetails";
import {
  WORK_AGREEMENT_ACCEPTED,
  WORK_AGREEMENT_DECLINED,
} from "../../../../constants";
import { FormKeys } from "../helpers";

interface Props {
  targetedShiftSpec: TargetedShiftSpec;
  contractId?: string;
  talentId: string;
}

export const WorkAgreementTargetedShifts = ({
  targetedShiftSpec,
  contractId,
  talentId,
}: Props) => {
  const { t } = useTranslation(["talentProfile", "common", "payslips"]);
  const dispatch = useAppDispatch();
  const { watch, setValue, getValues } = useFormContext();
  const { confirm } = useConfirmationDialog();

  const status = getValues(FormKeys.Status);
  const isReadOnly: boolean =
    status === WORK_AGREEMENT_ACCEPTED || status === WORK_AGREEMENT_DECLINED;

  const tesId: string = watch(FormKeys.TesId);
  const workTitleId: string = watch(FormKeys.WorkTitleId);
  const eezyCompanyId: string = watch(FormKeys.EezyCompanyId);
  const salaryRows: TargetedShiftSalaryRow[] = watch(
    FormKeys.TargetedShiftSalarySpecs,
  );
  const workStarts = watch(FormKeys.WorkStarts);
  const workEnds = watch(FormKeys.WorkEnds);

  const tesTranslations = useAppSelector((state) => state.localized.tes);
  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const employmentAttributesForEditing = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );

  useEffect(() => {
    if (talentId && workTitleId && tesId) {
      dispatch(
        fetchEmploymentAttributesForEditing({ talentId, workTitleId, tesId }),
      );
    }
  }, [talentId, workTitleId, tesId, dispatch]);

  const salaryZones =
    employmentAttributesForEditing.attributes?.tesId === tesId
      ? employmentAttributesForEditing.attributes?.salaryZones
      : undefined;

  const [eezyCompanyName, setEezyCompanyName] = useState<string>("");

  useEffect(() => {
    if (!eezyCompanyId) return;
    dispatch(fetchEezyCompanies({})).then((action) => {
      if (isFulfilled(action)) {
        const found = action.payload.find(
          (c) => c.eezyCompanyId === eezyCompanyId,
        );
        if (found) setEezyCompanyName(found.name);
      }
    });
  }, [eezyCompanyId, dispatch]);

  const [dialogState, setDialogState] =
    useState<TargetedShiftSalaryDialogState>(null);

  const spec: TargetedShiftSpec = {
    version: 0,
    targetingRules: targetedShiftSpec.targetingRules,
    salaries: salaryRows ?? [],
    variationValidationAssignmentId: null,
  };

  const primaryCustomerOrg =
    targetedShiftSpec.targetingRules[0]?.custOrgs[0]?.name ??
    targetedShiftSpec.targetingRules[0]?.custOrgs[0]?.id ??
    "-";

  const customerCompany = targetedShiftSpec.targetingRules[0]?.custCompany;

  const rulesForTable: RuleFormValues[] = targetedShiftSpec.targetingRules.map(
    (rule: TargetingKeys) => ({
      id: buildSalaryKey(rule),
      customerCompany: rule.custCompany,
      customerOrganization: rule.custOrgs[0] ?? null,
      workLocations: rule.workLocs,
      workTitles: rule.workTitles.map((id) => ({
        id,
        name: workTitleTranslations[id] ?? id,
      })),
      isDefaultRule: false,
    }),
  );

  const today = new Date();

  const tableData: DataItem[] = (salaryRows ?? []).map((row) => {
    const activeRange =
      getActiveSalaryRange(row.salary, today) ?? row.salary.ranges.at(0);
    const multipleRanges = hasMultipleDateRanges(row.salary);

    const dateDisplay = activeRange ? (
      <Box display="flex" alignItems="center" gap={0.5} component="span">
        {format(new Date(activeRange.from), leadingZerosDateFormat)}
        {multipleRanges && (
          <InfoTooltip
            title={t("talentProfile:multipleSalariesIndicatorTooltip")}
            variant="warning"
            placement="top"
          />
        )}
      </Box>
    ) : (
      "-"
    );

    const totalSalary = (
      <ZonedSalaryValue
        defaultValue={
          activeRange?.components
            ? calculateTotalSalary(activeRange.components)
            : "-"
        }
        zones={salaryZones}
        zoneValues={calculateZonedTotalSalary(activeRange?.zonedComponents)}
      />
    );

    return {
      values: {
        date: dateDisplay,
        organisation: row.keys.custOrgs.length
          ? row.keys.custOrgs.map((o) => o.name).join(", ")
          : t("talentProfile:allOrganizations"),
        workLocation: row.keys.workLocs.length
          ? row.keys.workLocs.map((wl) => wl.name).join(", ")
          : t("talentProfile:allWorkLocations"),
        workTitle: row.keys.workTitles.length
          ? row.keys.workTitles
              .map((id) => workTitleTranslations?.[id] ?? id)
              .join(", ")
          : t("talentProfile:allWorkTitles"),
        totalSalary,
      },
      id: buildSalaryKey(row.keys),
      deletable: true,
    };
  });

  const salaryHeaders = [
    { text: t("talentProfile:date") },
    { text: t("talentProfile:organization") },
    { text: t("common:workLocation") },
    { text: t("common:workTitle") },
    { text: t("talentProfile:totalSalary") },
  ];

  const handleOpenEditModal = (_: ContentKey, id?: string) => {
    if (id) {
      const row = (salaryRows ?? []).find((r) => buildSalaryKey(r.keys) === id);
      if (row) setDialogState({ mode: "edit", editingKey: row.keys });
    } else {
      setDialogState({ mode: "add" });
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t("common:confirmDelete"),
      contentText: t("talentProfile:confirmDeletingTargetedShiftSalary"),
      confirmText: t("common:delete"),
      cancelText: t("common:cancel"),
    });
    if (!confirmed) return;

    const updated = (salaryRows ?? []).filter(
      (r) => buildSalaryKey(r.keys) !== id,
    );
    setValue(FormKeys.TargetedShiftSalarySpecs, updated, { shouldDirty: true });
  };

  return (
    <>
      <FormSection title={t("talentProfile:targetedShifts.targetedShifts")}>
        <FormGridRow
          label={t("payslips:eezyCompany")}
          labelSize={3}
          fieldSize={9}
          GridFieldProps={{ pl: 2 }}
        >
          <Typography>{eezyCompanyName}</Typography>
        </FormGridRow>
        <FormGridRow
          label={t("common:tes")}
          labelSize={3}
          fieldSize={9}
          GridFieldProps={{ pl: 2 }}
        >
          <Typography>{tesTranslations[tesId] ?? tesId}</Typography>
        </FormGridRow>
        <FormGridRow
          label={t("common:customerOrganization")}
          labelSize={3}
          fieldSize={9}
          GridFieldProps={{ pl: 2 }}
        >
          <Typography>{primaryCustomerOrg}</Typography>
        </FormGridRow>
        <FormGridRow labelSize={3} fieldSize={9} GridFieldProps={{ pl: 2 }}>
          {/* <Grid item container xs={12} pt={2}> */}
          <RulesTable
            rules={rulesForTable}
            readOnly={true}
            editRow={() => {}}
            deleteRow={() => {}}
          />
          {/* </Grid> */}
        </FormGridRow>
      </FormSection>

      <FormSection title={t("talentProfile:targetedShiftSalaries")}>
        <FormGridRow labelSize={3} fieldSize={9} GridFieldProps={{ pl: 2 }}>
          <FormTable
            data={tableData}
            headers={salaryHeaders}
            contentKey=""
            disableEditRow={isReadOnly}
            handleOpenEditModal={handleOpenEditModal}
            handleDelete={isReadOnly ? undefined : handleDelete}
            addButtonLabel={
              !isReadOnly && targetedShiftSpec.targetingRules.length > 0
                ? t("talentProfile:addTargetedShiftSalary")
                : null
            }
          />
        </FormGridRow>

        <TargetedShiftSalaryDialog
          mode="local"
          open={dialogState !== null}
          tesId={tesId}
          talentId={talentId}
          workTitleId={workTitleId}
          spec={spec}
          canEdit={!isReadOnly}
          contractId={contractId}
          customerCompany={customerCompany}
          minDate={workStarts ?? null}
          maxDate={workEnds ?? null}
          onClose={() => setDialogState(null)}
          onSaved={(updated) => {
            setValue(FormKeys.TargetedShiftSalarySpecs, updated.salaries, {
              shouldDirty: true,
            });
            setDialogState(null);
          }}
          editingKey={
            dialogState?.mode === "edit" ? dialogState.editingKey : null
          }
        />
      </FormSection>
    </>
  );
};
